import { type InjectedFormikProps } from "formik";
import { Component } from "react";

import { BTCheckbox, BTPopover } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";

import { CheckAllState, NotificationTypes } from "types/enum";

import yup from "utilities/form/yup";
import { deepClone } from "utilities/object/object";
import { type KeyOfOrString } from "utilities/type/PropsOfType";

import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import {
    BTIconMailOutlined,
    BTIconMessageOutlined,
    BTIconMobileOutlined,
} from "commonComponents/btWrappers/BTIcon";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { type IBTColumnProps } from "commonComponents/btWrappers/BTTable/BTTable";
import { BTTable } from "commonComponents/btWrappers/BTTable/BTTable";
import { getCheckAllState } from "commonComponents/btWrappers/BTTable/BTTable.utils";
import { BTTitle } from "commonComponents/btWrappers/BTTitle/BTTitle";
import {
    type INotificationPreferenceData,
    type INotificationPreferenceRow,
    type INotificationTypes,
} from "commonComponents/entity/notificationPreferences/NotificationPreferencesTable.api.types";

import {
    getCategoryKey,
    getSubCategoryKey,
    showAllUserActivityCheckbox,
} from "entity/internalUser/InternalUserDetails/InternalUser.api.types";
import {
    type IRolePermissionFormValues,
    type Role,
} from "entity/internalUser/RolePermissions/RolePermissions.api.types";

import "./NotificationPreferencesTable.less";

interface INotificationPreferencesTableExpandableFormValues {
    notificationPreferences: INotificationPreferenceRow[];
}

interface INotificationPreferencesTableExpandableProps {
    disabled?: boolean;
}

interface INotificationPreferencesTableExpandableState {
    expandedCategoryRowKeys: string[];
    expandedGroupRowKeys: string[];
}

export class NotificationPreferencesTableExpandable extends Component<
    InjectedFormikProps<
        INotificationPreferencesTableExpandableProps,
        INotificationPreferencesTableExpandableFormValues
    >,
    INotificationPreferencesTableExpandableState
> {
    state: Readonly<INotificationPreferencesTableExpandableState> = {
        expandedCategoryRowKeys: [],
        expandedGroupRowKeys: [],
    };

    private toggleAll(propName: KeyOfOrString<INotificationTypes>, isChecked: boolean) {
        const values = deepClone(this.props.values);
        values.notificationPreferences.forEach((pref) => {
            if (pref[propName] !== null) {
                pref[propName] = isChecked;
            }
        });
        this.props.setFieldValue("notificationPreferences", values.notificationPreferences);
    }

    private toggleCategory(
        propName: KeyOfOrString<INotificationTypes>,
        categoryName: string,
        isChecked: boolean,
    ) {
        const values = deepClone(this.props.values);
        const groups = values.notificationPreferences.filter(
            (group) => group.canView && group.parent === categoryName,
        );
        groups.forEach((group) => {
            const prefs = values.notificationPreferences.filter(
                (pref) => pref.canView && pref.parent === group.groupName,
            );
            prefs.forEach((pref) => {
                if (pref[propName] !== null) {
                    pref[propName] = isChecked;
                }
            });
        });

        this.props.setFieldValue("notificationPreferences", values.notificationPreferences);
    }

    private toggleGroup(
        propName: KeyOfOrString<INotificationTypes>,
        groupName: string,
        isChecked: boolean,
    ) {
        const values = deepClone(this.props.values);
        values.notificationPreferences
            .filter((pref) => pref.parent === groupName)
            .forEach((pref) => {
                if (pref[propName] !== null) {
                    pref[propName] = isChecked;
                }
            });
        this.props.setFieldValue("notificationPreferences", values.notificationPreferences);
    }

    private getGroupCheckedAllState = (
        groupName: string,
        propName: KeyOfOrString<INotificationTypes>,
    ) => {
        const children = this.props.values.notificationPreferences.filter(
            (pref) => pref.canView && pref.parent === groupName && pref[propName] !== null,
        );
        const checked = children.filter((pref) => pref[propName]);
        return checked.length === children.length;
    };

    private getGroupCheckedAnyState = (
        groupName: string,
        propName: KeyOfOrString<INotificationTypes>,
    ) => {
        const children = this.props.values.notificationPreferences.filter(
            (pref) => pref.canView && pref.parent === groupName && pref[propName] !== null,
        );
        const checked = children.filter((pref) => pref[propName]);
        return checked.length !== children.length && checked.length > 0;
    };

    private getCategoryCheckedAllState = (
        groupName: string,
        propName: KeyOfOrString<INotificationTypes>,
    ) => {
        const children = this.props.values.notificationPreferences.filter(
            (pref) => pref.canView && pref.parent === groupName && pref[propName] !== null,
        );
        const checked = children
            .map((group) => this.getGroupCheckedAllState(group.groupName!, propName))
            .filter((group) => group);
        return checked.length === children.length;
    };

    private getCategoryCheckedAnyState = (
        groupName: string,
        propName: KeyOfOrString<INotificationTypes>,
    ) => {
        const children = this.props.values.notificationPreferences.filter(
            (pref) => pref.canView && pref.parent === groupName && pref[propName] !== null,
        );
        const checked = children
            .map((group) => this.getGroupCheckedAllState(group.groupName!, propName))
            .filter((group) => group);
        const indeterminate = children
            .map((group) => this.getGroupCheckedAnyState(group.groupName!, propName))
            .filter((group) => group);
        return (
            checked.length !== children.length && (checked.length > 0 || indeterminate.length > 0)
        );
    };

    private getCheckboxNode(
        value: boolean,
        record: INotificationPreferenceRow,
        propertyName: KeyOfOrString<INotificationTypes>,
    ): React.ReactNode {
        if (!record.isCategoryRow && !record.isGroupRow) {
            let alwaysOnKey: keyof INotificationPreferenceRow | undefined;
            let channelName: string | undefined;
            switch (propertyName) {
                case "emailEnabled":
                    alwaysOnKey = "alwaysOnEmail";
                    channelName = "email";
                    break;
                case "textEnabled":
                    alwaysOnKey = "alwaysOnText";
                    channelName = "text";
                    break;
                case "pushEnabled":
                    alwaysOnKey = "alwaysOnPush";
                    channelName = "push";
                    break;
                default:
                    alwaysOnKey = undefined;
                    channelName = undefined;
            }
            if (alwaysOnKey && record[alwaysOnKey]) {
                return (
                    <BTPopover
                        content={`${
                            record.name
                        } notifications via ${channelName!} are always enabled`}
                        placement="top"
                    >
                        <BTCheckbox
                            id={`notificationPreferences.${record.index!}.${propertyName}.alwaysOn`}
                            className="toggle-checkbox"
                            data-testid={`notificationPreferences.${record.index!}.${propertyName}.alwaysOn`}
                            checked={true}
                            disabled={true}
                        />
                    </BTPopover>
                );
            }
        }

        if (value !== null) {
            if (record.isCategoryRow) {
                const groups = this.props.values.notificationPreferences.filter(
                    (pref) =>
                        pref.canView &&
                        pref.parent === record.groupName! &&
                        pref[propertyName] !== null,
                );
                if (groups.length > 0) {
                    return (
                        <BTCheckbox
                            id={`notificationPreferences.${record.index!}.${propertyName}`}
                            data-testid={propertyName}
                            checked={this.getCategoryCheckedAllState(
                                record.groupName!,
                                propertyName,
                            )}
                            indeterminate={this.getCategoryCheckedAnyState(
                                record.groupName!,
                                propertyName,
                            )}
                            onChange={(e) =>
                                this.toggleCategory(
                                    propertyName,
                                    record.groupName!,
                                    e.target.checked,
                                )
                            }
                            disabled={this.props.disabled}
                            className="toggle-checkbox"
                        />
                    );
                }
                return null;
            } else if (record.isGroupRow) {
                const prefs = this.props.values.notificationPreferences.filter(
                    (pref) =>
                        pref.canView &&
                        pref.parent === record.groupName! &&
                        pref[propertyName] !== null,
                );
                if (prefs.length > 0) {
                    return (
                        <BTCheckbox
                            id={`notificationPreferences.${record.index!}.${propertyName}`}
                            className="toggle-checkbox"
                            data-testid={propertyName}
                            checked={this.getGroupCheckedAllState(record.groupName!, propertyName)}
                            indeterminate={this.getGroupCheckedAnyState(
                                record.groupName!,
                                propertyName,
                            )}
                            onChange={(e) =>
                                this.toggleGroup(propertyName, record.groupName!, e.target.checked)
                            }
                            disabled={this.props.disabled}
                        />
                    );
                }
                return null;
            } else {
                return (
                    <BTCheckbox
                        id={`notificationPreferences.${record.index!}.${propertyName}`}
                        className="toggle-checkbox"
                        data-testid={propertyName}
                        checked={value}
                        onChange={this.props.handleChange}
                        disabled={this.props.disabled}
                    />
                );
            }
        }
        return null;
    }

    private handleRowExpand = (record: INotificationPreferenceRow) => {
        if (!(record.isCategoryRow || record.isGroupRow) || record.groupName === null) {
            return;
        }

        const stateKey = record.isCategoryRow ? "expandedCategoryRowKeys" : "expandedGroupRowKeys";

        this.setState((prevState) => {
            return {
                ...prevState,
                [stateKey]: this.updateRowKeys(prevState[stateKey], record.groupName!),
            };
        });
    };

    private updateRowKeys = (rowKeys: string[], selectedKey: string) => {
        if (rowKeys.includes(selectedKey)) {
            return rowKeys.filter((k) => k !== selectedKey);
        }
        return [...rowKeys, selectedKey];
    };

    render() {
        const { values } = this.props;
        const visiblePreferences = values.notificationPreferences.filter(
            (row) => !row.isCategoryRow && !row.isGroupRow && row.canView,
        );
        const emailEnabledCheckedState = getCheckAllState(visiblePreferences, "emailEnabled");
        const textEnabledCheckedState = getCheckAllState(visiblePreferences, "textEnabled");
        const pushEnabledCheckedState = getCheckAllState(visiblePreferences, "pushEnabled");

        const headers: INotificationPreferenceRow[] = [];
        headers.push({
            groupName: "",
            name: "All notifications",
            emailEnabled: true,
            textEnabled: true,
            pushEnabled: true,
            allUsersEnabled: false,
            isCategoryRow: false,
            isGroupRow: false,
            canView: true,
            canViewAllUserActivity: true,
            parent: null,
            index: 1,
        });

        const nameHeader: IBTColumnProps<INotificationPreferenceRow> = {
            title: "",
            dataIndex: "name",
            render: (_, record) => {
                return <BTTitle>{record.name}</BTTitle>;
            },
        };
        const emailHeader: IBTColumnProps<INotificationPreferenceRow> = {
            title: (
                <BTRow justify="center" align="middle" gutter={4}>
                    <BTCol>
                        <BTIconMailOutlined />
                    </BTCol>
                    <BTCol>Email</BTCol>
                </BTRow>
            ),
            dataIndex: "emailEnabled",
            align: "center",
            render: () => {
                return (
                    <BTCheckbox
                        id="selectAllEmail"
                        data-testid="selectAllEmail"
                        checked={emailEnabledCheckedState === CheckAllState.AllChecked}
                        indeterminate={emailEnabledCheckedState === CheckAllState.Indeterminate}
                        onChange={(e) => this.toggleAll("emailEnabled", e.target.checked)}
                        className="toggle-checkbox"
                        disabled={this.props.disabled}
                    />
                );
            },
            className: "NotificationPreferencesTableExpandable-CheckboxColumn",
        };
        const textHeader: IBTColumnProps<INotificationPreferenceRow> = {
            title: (
                <BTRow justify="center" align="middle" gutter={4}>
                    <BTCol>
                        <BTIconMessageOutlined />
                    </BTCol>
                    <BTCol>Text</BTCol>
                </BTRow>
            ),
            align: "center",
            dataIndex: "textEnabled",
            render: () => {
                return (
                    <BTCheckbox
                        id="selectAllText"
                        data-testid="selectAllText"
                        checked={textEnabledCheckedState === CheckAllState.AllChecked}
                        indeterminate={textEnabledCheckedState === CheckAllState.Indeterminate}
                        onChange={(e) => this.toggleAll("textEnabled", e.target.checked)}
                        className="toggle-checkbox"
                        disabled={this.props.disabled}
                    />
                );
            },
            className: "NotificationPreferencesTableExpandable-CheckboxColumn",
        };
        const pushHeader: IBTColumnProps<INotificationPreferenceRow> = {
            title: (
                <BTRow justify="center" align="middle" gutter={4}>
                    <BTCol>
                        <BTIconMobileOutlined />
                    </BTCol>
                    <BTCol>
                        <BTPopover
                            placement="left"
                            data-testid="notificationPreferences.pushColumn-help"
                            id="notificationPreferences.pushColumn-help"
                            content="This setting allows push notifications on both your browser and mobile devices, if applicable."
                        >
                            <span className="ColumnHeaderTooltipTrigger">Push</span>
                        </BTPopover>
                    </BTCol>
                </BTRow>
            ),
            align: "center",
            dataIndex: "pushEnabled",
            render: () => {
                return (
                    <BTCheckbox
                        id="selectAllPush"
                        data-testid="selectAllPush"
                        checked={pushEnabledCheckedState === CheckAllState.AllChecked}
                        indeterminate={pushEnabledCheckedState === CheckAllState.Indeterminate}
                        onChange={(e) => this.toggleAll("pushEnabled", e.target.checked)}
                        className="toggle-checkbox"
                        disabled={this.props.disabled}
                    />
                );
            },
            className: "NotificationPreferencesTableExpandable-CheckboxColumn",
        };

        const groupColumn: IBTColumnProps<INotificationPreferenceRow> = {
            title: "",
            dataIndex: "groupName",
            onCell: (record) => {
                return {
                    onClick: () => {
                        this.handleRowExpand(record);
                    },
                };
            },
            render: (_, record) => {
                if (record.isCategoryRow || record.isGroupRow) {
                    return {
                        children: record.groupName,
                    };
                } else {
                    return null;
                }
            },
            className: "NotificationPreferencesTableExpandable-GroupColumn",
        };

        const nameColumn: IBTColumnProps<INotificationPreferenceRow> = {
            title: "",
            dataIndex: "name",
            render: (_, record) => {
                return (
                    <span
                        className={cn({
                            "child-notification-indent":
                                record.type === NotificationTypes.BillPaymentMadeFromAccounting,
                        })}
                    >
                        {record.name}
                        {record.tooltipText && (
                            <BTPopover content={record.tooltipText} placement="top" />
                        )}
                    </span>
                );
            },
        };

        const emailColumn: IBTColumnProps<INotificationPreferenceRow> = {
            dataIndex: "emailEnabled",
            align: "center",
            render: (value, record) => this.getCheckboxNode(value, record, "emailEnabled"),
            className: "NotificationPreferencesTableExpandable-CheckboxColumn",
        };

        const textColumn: IBTColumnProps<INotificationPreferenceRow> = {
            align: "center",
            dataIndex: "textEnabled",
            render: (value, record) => this.getCheckboxNode(value, record, "textEnabled"),
            className: "NotificationPreferencesTableExpandable-CheckboxColumn",
        };

        const pushColumn: IBTColumnProps<INotificationPreferenceRow> = {
            align: "center",
            dataIndex: "pushEnabled",
            render: (value, record) => this.getCheckboxNode(value, record, "pushEnabled"),
            className: "NotificationPreferencesTableExpandable-CheckboxColumn",
        };

        const allColumn: IBTColumnProps<INotificationPreferenceRow> = {
            title: "",
            align: "center",
            dataIndex: "allUsersEnabled",
            width: 200,
            render: (value, record) => {
                const checkBox = this.getCheckboxNode(value, record, "allUsersEnabled");
                if (checkBox && record.canViewAllUserActivity) {
                    return (
                        <BTRow align="middle" gutter={8}>
                            <BTCol>{checkBox}</BTCol>
                            <BTCol>All user activity</BTCol>
                            <BTPopover
                                content={
                                    commentNotificationTypes.includes(record.type ?? 0)
                                        ? "Never miss a comment, you'll get notifications for all comment activity for this feature"
                                        : "You will receive additional notifications about this kind of activity from all other Internal Users."
                                }
                            />
                        </BTRow>
                    );
                }
                return null;
            },
            className: "NotificationPreferencesTableExpandable-CheckboxColumn",
        };

        const headerColumnProps: IBTColumnProps<INotificationPreferenceRow>[] = [];
        headerColumnProps.push(nameHeader);
        headerColumnProps.push(emailHeader);
        headerColumnProps.push(textHeader);
        headerColumnProps.push(pushHeader);

        const categoryColumnProps: IBTColumnProps<INotificationPreferenceRow>[] = [];
        categoryColumnProps.push(groupColumn);
        categoryColumnProps.push(emailColumn);
        categoryColumnProps.push(textColumn);
        categoryColumnProps.push(pushColumn);

        const groupColumnProps: IBTColumnProps<INotificationPreferenceRow>[] = [];
        groupColumnProps.push(groupColumn);
        groupColumnProps.push(emailColumn);
        groupColumnProps.push(textColumn);
        groupColumnProps.push(pushColumn);

        const prefColumnProps: IBTColumnProps<INotificationPreferenceRow>[] = [];
        prefColumnProps.push(nameColumn);
        if (
            values.notificationPreferences.filter((pref) => pref.allUsersEnabled !== null).length >
            0
        ) {
            prefColumnProps.push(allColumn);
        }
        prefColumnProps.push(emailColumn);
        prefColumnProps.push(textColumn);
        prefColumnProps.push(pushColumn);

        return (
            <div className="NotificationPreferencesTableContainer">
                <BTTable
                    rowKey="name"
                    dataSource={headers}
                    columns={headerColumnProps}
                    pagination={false}
                    bordered={false}
                    className="NotificationPreferencesTableHeader"
                />
                <BTTitle>Advanced notifications options</BTTitle>
                <BTTable<INotificationPreferenceRow>
                    rowKey="groupName"
                    dataSource={values.notificationPreferences.filter(
                        (p) => p.canView && p.isCategoryRow,
                    )}
                    columns={categoryColumnProps}
                    pagination={false}
                    showHeader={false}
                    className="NotificationPreferencesTableExpandable"
                    rowClassName={(record) =>
                        record.isCategoryRow
                            ? "NotificationPreferencesTableExpandable-category-row"
                            : ""
                    }
                    expandable={{
                        rowExpandable: (record) => record.isCategoryRow,
                        expandedRowKeys: this.state.expandedCategoryRowKeys,
                        onExpand: (expanded, record) => this.handleRowExpand(record),
                        expandedRowRender: (record) => {
                            return (
                                <BTTable<INotificationPreferenceRow>
                                    rowKey="groupName"
                                    dataSource={values.notificationPreferences.filter(
                                        (p) =>
                                            p.canView &&
                                            p.isGroupRow &&
                                            p.parent === record.groupName,
                                    )}
                                    columns={groupColumnProps}
                                    pagination={false}
                                    showHeader={false}
                                    rowClassName={(record) =>
                                        record.isGroupRow
                                            ? "NotificationPreferencesTableExpandable-row"
                                            : ""
                                    }
                                    expandable={{
                                        rowExpandable: (record) => record.isGroupRow,
                                        expandedRowKeys: this.state.expandedGroupRowKeys,
                                        onExpand: (expanded, record) =>
                                            this.handleRowExpand(record),
                                        expandedRowRender: (record) => {
                                            return (
                                                <BTTable
                                                    rowKey="name"
                                                    dataSource={values.notificationPreferences.filter(
                                                        (p) =>
                                                            p.canView &&
                                                            !p.isCategoryRow &&
                                                            !p.isGroupRow &&
                                                            p.parent === record.groupName,
                                                    )}
                                                    columns={prefColumnProps}
                                                    pagination={false}
                                                    showHeader={false}
                                                    rowClassName={(record) =>
                                                        !record.isCategoryRow && !record.isGroupRow
                                                            ? "NotificationPreferencesTableExpandable-row"
                                                            : ""
                                                    }
                                                />
                                            );
                                        },
                                    }}
                                />
                            );
                        },
                    }}
                />
            </div>
        );
    }
}

export const setRoleViewPermissions = (
    notificationPrefs: INotificationPreferenceRow[],
    role: IRolePermissionFormValues,
) => {
    const clonedNotificationPrefs = notificationPrefs.map((x) => {
        return { ...x };
    });
    const categories = clonedNotificationPrefs.filter((np) => np.isCategoryRow);
    categories.forEach((category) => {
        const categoryName = category.groupName!;
        const groups = clonedNotificationPrefs.filter(
            (np) => np.isGroupRow && np.parent === category.groupName,
        );
        groups.forEach((group) => {
            const groupName = group.groupName!;
            const prefs = clonedNotificationPrefs.filter(
                (np) => !np.isCategoryRow && !np.isGroupRow && np.parent === groupName,
            );
            prefs.forEach((pref) => {
                pref.canView =
                    role.categories[getCategoryKey(categoryName)].subCategories[
                        getSubCategoryKey(groupName)
                    ]?.permissions.view.enabled ?? true;

                if (
                    pref.allUsersEnabled !== null &&
                    !showAllUserActivityCheckbox(pref.type!, role)
                ) {
                    pref.canViewAllUserActivity = false;
                } else {
                    pref.canViewAllUserActivity = true;
                }
            });
            group.canView = prefs.filter((pref) => pref.canView).length > 0;
        });
        category.canView = groups.filter((group) => group.canView).length > 0;
    });

    return clonedNotificationPrefs;
};

export const mapPreferencesToRowsExpandable = (data: INotificationPreferenceData, role?: Role) => {
    let rowValues: INotificationPreferenceRow[] = [];
    let newIndex = 0;
    data.categories.forEach((category) => {
        category.groups.forEach((group, groupIndex) => {
            if (groupIndex === 0 && category.category !== null) {
                rowValues.push({
                    groupName: category.category.toUpperCase(),
                    name: "",
                    emailEnabled: null,
                    textEnabled: null,
                    pushEnabled: null,
                    allUsersEnabled: null,
                    isCategoryRow: true,
                    isGroupRow: false,
                    canView: true,
                    canViewAllUserActivity: true,
                    parent: null,
                    index: newIndex,
                });
                newIndex++;
            }
            group.preferences.forEach((pref, index) => {
                if (index === 0) {
                    rowValues.push({
                        groupName: group.name,
                        name: "",
                        emailEnabled: null,
                        textEnabled: null,
                        pushEnabled: null,
                        allUsersEnabled: null,
                        isCategoryRow: false,
                        isGroupRow: true,
                        canView: true,
                        canViewAllUserActivity: true,
                        parent: category.category!.toUpperCase(),
                        index: newIndex,
                    });
                    newIndex++;
                }
                rowValues.push({
                    groupName: null,
                    name: pref.name,
                    emailEnabled: pref.emailEnabled,
                    textEnabled: pref.textEnabled,
                    pushEnabled: pref.pushEnabled,
                    allUsersEnabled: pref.allUsersEnabled,
                    tab: group.tabType,
                    type: pref.type,
                    isCategoryRow: false,
                    isGroupRow: false,
                    canView: pref.canView ?? true,
                    canViewAllUserActivity: true,
                    parent: group.name,
                    index: newIndex,
                    tooltipText: pref.tooltipText,
                    alwaysOnEmail: pref.alwaysOnEmail,
                    alwaysOnText: pref.alwaysOnText,
                    alwaysOnPush: pref.alwaysOnPush,
                });
                newIndex++;
            });
        });
    });

    if (role) {
        rowValues = setRoleViewPermissions(rowValues, role);
    }

    // Add checkboxes to groups where applicable
    const groups = rowValues.filter((row) => row.canView && row.isGroupRow);
    groups.forEach((group) => {
        const prefs = rowValues.filter((row) => row.canView && row.parent === group.groupName!);
        if (prefs.filter((pref) => pref.emailEnabled !== null).length > 0) {
            group.emailEnabled = true;
        }
        if (prefs.filter((pref) => pref.textEnabled !== null).length > 0) {
            group.textEnabled = true;
        }
        if (prefs.filter((pref) => pref.pushEnabled !== null).length > 0) {
            group.pushEnabled = true;
        }
    });

    // add checkboxes to categories where applicable
    const categories = rowValues.filter((row) => row.canView && row.isCategoryRow);
    categories.forEach((category) => {
        const groups = rowValues.filter((row) => row.canView && row.isGroupRow);
        if (groups.filter((group) => group.emailEnabled !== null).length > 0) {
            category.emailEnabled = true;
        }
        if (groups.filter((group) => group.textEnabled !== null).length > 0) {
            category.textEnabled = true;
        }
        if (groups.filter((group) => group.pushEnabled !== null).length > 0) {
            category.pushEnabled = true;
        }
    });

    return rowValues;
};

export const NotificationPreferencesTableExpandableValidators = yup
    .array()
    .label("Notification Preferences")
    .of(
        yup.object().shape<INotificationPreferenceRow>({
            groupName: yup.string().nullable(true).label("Group"),
            name: yup.string().label("Preference"),
            emailEnabled: yup.bool().nullable(true).label("Email"),
            textEnabled: yup.bool().nullable(true).label("Text"),
            pushEnabled: yup.bool().nullable(true).label("Push"),
            allUsersEnabled: yup.bool().nullable(true).label("All Users Enabled"),
            tab: yup.number().label("Tab"),
            type: yup.number().label("Type"),
            isCategoryRow: yup.bool().label("Category Row"),
            isGroupRow: yup.bool().label("Group Row"),
            canView: yup.bool().label("Can View"),
            canViewAllUserActivity: yup.bool().label("Can View All User Permission"),
            parent: yup.string().nullable(true).label("Parent"),
            index: yup.number().label("Index"),
        }),
    );

const commentNotificationTypes = [
    NotificationTypes.SELECTION_DISCUSSION_ADDED,
    NotificationTypes.CHANGEORDER_DISCUSSION_ADDED,
    NotificationTypes.PO_DISCUSSION_ADDED,
    NotificationTypes.WARRANTY_DISCUSSION_ADDED,
    NotificationTypes.Bid_DiscussionAdded,
    NotificationTypes.Daily_Log_Discussion_Added,
    NotificationTypes.Schedule_Item_Discussion_Added,
    NotificationTypes.TO_DO_DISCUSSION_ADDED,
    NotificationTypes.OwnerPaymentDiscussionAdded,
    NotificationTypes.PhotoCommentAdded,
    NotificationTypes.DocumentCommentAdded,
    NotificationTypes.VideoCommentAdded,
    NotificationTypes.CREDITMEMO_DISCUSSION_ADDED,
    NotificationTypes.BILL_DISCUSSION_ADDED,
    NotificationTypes.DepositDiscussionAdded,
    NotificationTypes.ClientUpdateDiscussionAdded,
    NotificationTypes.Task_Discussion_Added,
    NotificationTypes.SELECTION_OPTION_DISCUSSION_ADDED,
];
